
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SYSTEM_NOTIFICATION_TYPES } from "@/constants/notificationTypes";
import { SupabaseNotification } from "@/types/notifications";

/**
 * Hook for fetching notification data
 */
export const useNotificationsData = (viewMode: "active" | "archived") => {
  const { 
    data: notifications = [], 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['notifications', viewMode],
    queryFn: async () => {
      try {
        // Optimized query: JOIN recipients in single query instead of N+1 queries
        const { data, error } = await supabase
          .from('notifications')
          .select(`
            *,
            notification_recipients (
              recipient_type,
              recipient_name
            )
          `)
          .eq('archived', viewMode === "archived")
          .eq('is_deleted', false)
          .not('type', 'in', `(${SYSTEM_NOTIFICATION_TYPES.join(',')})`)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // Transform data to match expected format
        const notificationsWithRecipients = (data || []).map((notification: any) => {
          const recipients = {
            internal: notification.notification_recipients
              ?.filter((r: any) => r.recipient_type === 'internal')
              .map((r: any) => r.recipient_name) || [],
            external: notification.notification_recipients
              ?.filter((r: any) => r.recipient_type === 'external')
              .map((r: any) => r.recipient_name) || []
          };
          
          // Remove notification_recipients array from the object
          const { notification_recipients, ...notificationData } = notification;
          
          return { 
            ...notificationData, 
            recipients
          } as SupabaseNotification;
        });
        
        return notificationsWithRecipients;
      } catch (error: any) {
        console.error("Error fetching notifications:", error);
        toast.error("Fout bij het ophalen van meldingen", {
          description: error.message
        });
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - rely on realtime for updates
  });

  return {
    notifications,
    isLoading,
    error
  };
};
